import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Store, Plus, Trash2, Edit, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import FileUpload from '../FileUpload';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';

const SellerProductsTab = ({ products, selectedBooth, myBooths, fetchData }) => {
  const { formatPrice } = useCurrency();
  const { t } = useTranslation();
  const [showProductModal, setShowProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [showDeleteProductConfirm, setShowDeleteProductConfirm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', description: '', price: '', image_url: '' });
  const [pendingProductImage, setPendingProductImage] = useState(null);

  const resetProductForm = () => { setProductForm({ name: '', description: '', price: '', image_url: '' }); setPendingProductImage(null); };

  const addProduct = async (e) => {
    e.preventDefault();
    const booth = selectedBooth || myBooths[0];
    if (!booth) { toast.error('No booth selected'); return; }
    try {
      const productRes = await axios.post(`${API}/products`, {
        booth_id: booth.id, name: productForm.name, description: productForm.description,
        price: productForm.price ? parseFloat(productForm.price) : null, image_url: ''
      });
      if (pendingProductImage) {
        try {
          const formData = new FormData();
          formData.append('file', pendingProductImage);
          await axios.post(`${API}/upload/product/${productRes.data.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (uploadError) { console.error('Image upload failed:', uploadError); toast.error('Product created but image upload failed'); }
      }
      toast.success('Product added');
      setShowProductModal(false);
      resetProductForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add product'); }
  };

  const openEditProduct = (product) => {
    setSelectedProduct(product);
    setProductForm({ name: product.name, description: product.description, price: product.price?.toString() || '', image_url: product.image_url || '' });
    setShowEditProductModal(true);
  };

  const updateProduct = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;
    try {
      await axios.put(`${API}/products/${selectedProduct.id}`, {
        booth_id: selectedProduct.booth_id, name: productForm.name, description: productForm.description,
        price: productForm.price ? parseFloat(productForm.price) : null, image_url: productForm.image_url
      });
      toast.success('Product updated');
      setShowEditProductModal(false);
      setSelectedProduct(null);
      resetProductForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update product'); }
  };

  const deleteProduct = async () => {
    if (!productToDelete) return;
    try {
      await axios.delete(`${API}/products/${productToDelete.id}`);
      toast.success('Product deleted');
      setShowDeleteProductConfirm(false);
      setProductToDelete(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete product'); }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('dashboard.products_services')}</CardTitle>
            <CardDescription>{t('dashboard.products_services_desc')}</CardDescription>
          </div>
          <Button onClick={() => setShowProductModal(true)}>
            <Plus className="mr-2 h-4 w-4" /> {t('dashboard.add_product')}
          </Button>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <div className="text-center py-8">
              <Store className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">{t('dashboard.no_products')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((product) => (
                <Card key={product.id} className="relative overflow-hidden group">
                  {product.image_url ? (
                    <div className="w-full aspect-[3/4] bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
                      <img src={product.image_url.startsWith('/api') ? `${API}${product.image_url.replace('/api', '')}` : product.image_url}
                        alt={product.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-full aspect-[3/4] bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1 bg-white/90 rounded-lg p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => openEditProduct(product)} title="Edit"><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => { setProductToDelete(product); setShowDeleteProductConfirm(true); }} title="Delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <CardContent className="pt-3">
                    <h4 className="font-medium line-clamp-1">{product.name}</h4>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{product.description}</p>
                    {product.price && <p className="text-lg font-semibold text-primary mt-2">{formatPrice(product.price, true)}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Product Modal */}
      <Dialog open={showProductModal} onOpenChange={(open) => { setShowProductModal(open); if (!open) resetProductForm(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Product/Service</DialogTitle></DialogHeader>
          <form onSubmit={addProduct} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div><Label>Product Name</Label><Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required /></div>
            <div><Label>Description</Label><Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} required rows={2} /></div>
            <div><Label>Price (Optional)</Label><Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} /></div>
            <div>
              <Label>Product Image (Optional)</Label>
              <div className="mt-2">
                {pendingProductImage ? (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gradient-to-b from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center">
                    <img src={URL.createObjectURL(pendingProductImage)} alt="Preview" className="max-w-full max-h-full object-contain" />
                    <button type="button" onClick={() => setPendingProductImage(null)} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <label className="w-full h-48 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                    <ImageIcon className="w-10 h-10 text-slate-400 mb-2" /><span className="text-sm text-slate-500">Click to upload image</span><span className="text-xs text-slate-400 mt-1">Max 5MB</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) { if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Maximum size is 5MB'); return; } setPendingProductImage(file); } }} />
                  </label>
                )}
              </div>
            </div>
            <Button type="submit" className="w-full">Add Product</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Product Modal */}
      <Dialog open={showEditProductModal} onOpenChange={(open) => { setShowEditProductModal(open); if (!open) { setSelectedProduct(null); resetProductForm(); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Product/Service</DialogTitle></DialogHeader>
          <form onSubmit={updateProduct} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div><Label>Product Name</Label><Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required /></div>
            <div><Label>Description</Label><Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} required rows={2} /></div>
            <div><Label>Price (Optional)</Label><Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} /></div>
            <div>
              <Label>Product Image</Label>
              <FileUpload entityType="product" entityId={selectedProduct?.id} currentUrl={productForm.image_url}
                onUploadSuccess={(url) => setProductForm({ ...productForm, image_url: url })}
                onRemove={() => setProductForm({ ...productForm, image_url: '' })}
                accept="image/*" label="Upload Image" previewClassName="w-full h-48" />
            </div>
            <Button type="submit" className="w-full">Update Product</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Product Confirmation */}
      <Dialog open={showDeleteProductConfirm} onOpenChange={setShowDeleteProductConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Product</DialogTitle>
            <DialogDescription>Are you sure you want to delete <strong>{productToDelete?.name}</strong>? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteProductConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteProduct}>Delete Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SellerProductsTab;
